import { useState, useEffect, useContext, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TourProvider, useTour } from '@reactour/tour';
import { UserContext } from "../context/UserContext";
import { ThemeContext } from "../context/ThemeContext";
import { useSessionValidation } from "../hooks/useSessionValidation";
import SearchField from "./SearchField";
import HeaderTable from "./HeaderTable";
import ScanFields from "./ScanFields";
import LinesTable from "./LinesTable";
import ActionButtons from "./ActionButtons";
import { Header, Line, ScanFields as ScanFieldsType } from "../types";
import { useSearchHeaders } from "../hooks/useSearchHeaders";
import { useLineDetails } from "../hooks/useLineDetails";
import { useValidatedSnItem } from "../hooks/useValidatedSnItem";
import { useValidateSerialNumber } from "../hooks/useValidateSerialNumber";
import { useLineManager } from "./LineManager";
import { useSerialNumberScanner } from "./SerialNumberScanner";
import { useRadSites } from "../hooks/useRadSites";
import { useFromSiteWhid } from "../hooks/useFromSiteWhid";
import { useSaveHeaderService } from "../services/saveHeaderService";
import { useUpsertLines } from "../hooks/useUpsertLines";
import { useSubmitRequest } from "../hooks/useSubmitRequest";
import SerialSelectionPopup from "./SerialSelectionPopup";
import { useSubmitButtonState } from "../hooks/useSubmitButtonState";
import { useCreateLocator } from "../hooks/useCreateLocator";
import NewLineForm from "./NewLineForm";
import TourWidget from "./TourWidget";
import { useStageRemainingSerials } from "../utils/stageRemainingSerials";
import { createRadReceivingTourSteps } from "../utils/tourSteps";

interface SerialValidationResult {
  whid: string;
  organizationId: string;
  inventoryItemId: string;
  serialNumber: string;
  itemNumber: string;
  description: string;
  assetNumber: string;
  serialStatus: string;
  snRepairEligible: string;
}

interface DashboardContentProps {
  isDarkTheme: boolean;
  setIsDarkTheme: (value: boolean) => void;
}

const DashboardContent = ({ isDarkTheme, setIsDarkTheme }: DashboardContentProps) => {
  const { userId, responsibilityId, sessionId } = useContext(UserContext);
  const { validateSession } = useSessionValidation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const sessionValidated = useRef(false);
  const navigate = useNavigate();
  const { setIsOpen, setSteps, setCurrentStep, currentStep, isOpen } = useTour();
  const [searchTerm, setSearchTerm] = useState("");
  const [fromSiteWhid, setFromSiteWhid] = useState("");
  const [fromSiteWhidOrgId, setFromSiteWhidOrgId] = useState("");
  const [selectedHeader, setSelectedHeader] = useState<Header | undefined>();
  const [scanFields, setScanFields] = useState<ScanFieldsType>({
    scanSerialNumber: "",
    toSubinventory: "",
    pallet: "",
    cage: "",
    tote: "",
    toLocator: "",
    trackingNumber: "",
    toRad: "",
  });
  const [updatedLines, setUpdatedLines] = useState<Line[]>([]);
  const [scannedLineIds, setScannedLineIds] = useState<Set<string>>(new Set());
  const [updatedHeaders, setUpdatedHeaders] = useState<Header[]>([]);
  const [originalToSite, setOriginalToSite] = useState<string | undefined>();
  const [selectedHeaderOrgId, setSelectedHeaderOrgId] = useState<string | undefined>();
  const [showNewLineForm, setShowNewLineForm] = useState(false);
  const [serialNumberForForm, setSerialNumberForForm] = useState<string | undefined>();
  const [editLineData, setEditLineData] = useState<Line | undefined>(); // Add state for edit line data
  const [includeClosedOrders, setIncludeClosedOrders] = useState(false);
  const clearSerialNotFoundRef = useRef<(() => void) | null>(null);
  const clearLinesErrorsRef = useRef<(() => void) | null>(null);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [scanSerialError, setScanSerialError] = useState<string | null>(null);
  const [showSerialSelectionPopup, setShowSerialSelectionPopup] = useState(false);
  const [serialValidationResults, setSerialValidationResults] = useState<SerialValidationResult[]>([]);
  const [isFromPopupSelection, setIsFromPopupSelection] = useState(false);
  const [serialScanLoading, setSerialScanLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [headerSelectionWarning, setHeaderSelectionWarning] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const searchFieldRef = useRef<{ focusInput: () => void } | null>(null);
  const [savedTourStep, setSavedTourStep] = useState<number>(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const { headers: filteredHeaders, error, loading, searchHeaders, loadMoreHeaders, refreshSingleHeader, hasMore } = useSearchHeaders();
  const { lines: headerLines, error: linesError, loading: linesLoading } = useLineDetails(
    selectedHeader?.requestNumber
  );
  const { data: radSites, isLoading: radSitesLoading, fetchRadSites } = useRadSites();
  const { data: fromSiteWhidOptions, fetchFromSiteWhid } = useFromSiteWhid();

  const currentLines = useMemo(() => {
    return selectedHeader
      ? updatedLines.length > 0
        ? updatedLines
        : headerLines
      : [];
  }, [selectedHeader, updatedLines, headerLines]);

  const lineManager = useLineManager({
    lines: currentLines,
    onLinesUpdate: (lines) => {
      if (typeof lines === 'function') {
        setUpdatedLines(prevLines => {
          const result = lines(prevLines.length > 0 ? prevLines : headerLines);
          return result;
        });
      } else {
        setUpdatedLines(lines);
      }
    },
    selectedHeaderToOrg: selectedHeader?.toOrg,
  });

  const serialScanner = useSerialNumberScanner({
    lines: currentLines,
    scanFields,
    onLinesUpdate: (lines) => {
      setUpdatedLines(lines);
      // Call upsertLines API for lines updated through serial scanning
      // Track the first line as scanned (it gets moved to top) only if it was updated via scanning
      if (lines.length > 0 && lines[0].validatedSerialNumber) {
        setScannedLineIds(prev => new Set(prev).add(lines[0].id));
        // Call upsertLines API after successful line update from scanning
        callUpsertLinesAPI(lines[0]);
      }
    },
    onScanFieldsUpdate: setScanFields,
    onSerialNotFound: (serialNumber) => {
      console.log('Opening form for serial number:', serialNumber);
      setSerialNumberForForm(serialNumber);
      setIsFromPopupSelection(false); // Direct serial not found
      setShowNewLineForm(true);
    },
    onMultipleSerialResults: (results) => {
      setSerialValidationResults(results);
      setShowSerialSelectionPopup(true);
    },
    selectedHeaderFromSite: selectedHeader?.fromSite,
    selectedHeaderToOrg: selectedHeader?.toOrg,
    organizationName: selectedHeader?.toSite,
    onLoadingChange: setSerialScanLoading,
    onShowWarning: (message: string) => {
      setScanSerialError(message);
      setTimeout(() => setScanSerialError(null), 10000);
    }
  });

  useEffect(() => {
    if (sessionId === "" || sessionId === undefined || sessionId === null) {
      navigate("/unauthorized");
    } else {
      if (!sessionValidated.current) {
        sessionValidated.current = true;
        validateSession(
          {
            user_id: parseInt(userId),
            resp_id: parseInt(responsibilityId),
            session_id: parseInt(sessionId),
          },
          (response: any) => {
            if (
              response &&
              response?.userId === Number(userId) &&
              response?.sessionId === Number(sessionId)
            ) {
              setIsLoggedIn(true);
            } else {
              navigate("/unauthorized");
            }
          }
        );
      }
    }
  }, [
    sessionId,
    navigate,
    responsibilityId,
    userId,
    validateSession,
  ]);

  // Fetch RAD sites when user is logged in
  useEffect(() => {
    if (isLoggedIn) {
      fetchRadSites();
      fetchFromSiteWhid();
    }
  }, [isLoggedIn, fetchRadSites, fetchFromSiteWhid]);

  useEffect(() => {
    document.body.style.backgroundColor = isDarkTheme ? "#1a1a1a" : "#ffffff";
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.overflowX = "hidden";
    document.body.style.overflowY = "auto";
    document.documentElement.style.overflowX = "hidden";
    document.documentElement.style.overflowY = "auto";
  }, [isDarkTheme]);

  const handleSearchTermChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    if (!newSearchTerm.trim()) {
      searchHeaders("", includeClosedOrders, 1, 10, fromSiteWhidOrgId);
      setSelectedHeader(undefined);
      setScanFields({
        scanSerialNumber: "",
        toSubinventory: "",
        pallet: "",
        cage: "",
        tote: "",
        toLocator: "",
        trackingNumber: "",
        toRad: "",
      });
      setUpdatedLines([]);
    }
  };

  const handleSearch = () => {
    if (!fromSiteWhid) {
      setHeaderSelectionWarning("Please select From Site WHID before searching.");
      setTimeout(() => setHeaderSelectionWarning(null), 3000);
      return;
    }
    if (!searchTerm.trim()) {
      setHeaderSelectionWarning("Please enter Request/Tracking/Serial Number before clicking Search.");
      setTimeout(() => setHeaderSelectionWarning(null), 3000);
      // Focus search input
      setTimeout(() => {
        if (searchFieldRef.current) {
          searchFieldRef.current.focusInput();
        }
      }, 100);
      return;
    }
    
    searchHeaders(searchTerm, includeClosedOrders, 1, 10, fromSiteWhidOrgId);
    setSelectedHeader(undefined);
    setScanFields({ scanSerialNumber: "", toSubinventory: "", pallet: "", cage: "", tote: "", toLocator: "", trackingNumber: "", toRad: "" });
    setUpdatedLines([]);
    setUpdatedHeaders([]); // Clear modified header values
  };

  const { saveHeader, error: saveError } = useSaveHeaderService();
  const { upsertLines, error: upsertError } = useUpsertLines();
  const { submitRequest, error: submitRequestError } = useSubmitRequest();
  const { isSubmitDisabled } = useSubmitButtonState(currentLines, selectedHeader, hasSubmitted);
  const { createLocator } = useCreateLocator();
  const { stageRemainingItems } = useStageRemainingSerials();

  // Function to call upsertLines API after line update
  const callUpsertLinesAPI = async (updatedLine: Line) => {
    if (selectedHeader?.requestNumber) {
      try {
        await upsertLines(selectedHeader.requestNumber, [updatedLine], "New");
        console.log('upsertRepairLineDetails API called successfully for line:', updatedLine.id);
      } catch (error) {
        console.error('Failed to call upsertRepairLineDetails API:', error);
      }
    }
  };

  const handleHeaderClick = (header: Header) => {
    // Prevent header selection while lines are loading
    if (linesLoading) {
      return;
    }
    
    setSelectedHeader(header);
    setOriginalToSite(header.toSite);
    // Use existing selectedHeaderOrgId if available (preserves updated organizationId), otherwise use header.toOrg
    const orgIdToUse = selectedHeaderOrgId || header.toOrg;
    setSelectedHeaderOrgId(orgIdToUse);

    // Preserve cached TO RAD value, only clear other fields
    setScanFields(prev => ({ 
      ...prev,
      scanSerialNumber: "", 
      toSubinventory: "", 
      pallet: "", 
      cage: "", 
      tote: "", 
      toLocator: "", 
      trackingNumber: ""
      // Keep toRad from cache
    }));
    setUpdatedLines([]);
    setScannedLineIds(new Set());
    lineManager.clearNewRowIds();
    setHasSubmitted(false); // Reset submit state when new header is selected
  };

  const handleSubmit = async () => {
    // Validate that lines have validated serial numbers and required fields
    const hasValidatedLines = currentLines.some(line => 
      line.validatedSerialNumber && line.toSubinventory && line.toLocator
    );
    
    if (!hasValidatedLines) {
      setValidationError("Please scan the serial numbers to be received before saving or submitting.");
      return;
    }

    setSubmitLoading(true);
    setHasSubmitted(true); // Disable submit button immediately
    try {
      const headerToUpdate = updatedHeaders.find(h => h.requestNumber === selectedHeader!.requestNumber) || selectedHeader!;
      
      // Get TO RAD organizationId
      const toRadSite = radSites.find(site => site.organizationName === scanFields.toRad);
      const toRadOrganizationId = toRadSite?.organizationId;
      
      // Always call updatetoorgdetails API
      const headerResult = await saveHeader({
        selectedHeader: selectedHeader!,
        updatedHeaders,
        toRadOrganizationId
      });

      // Submit request as final step
      await submitRequest(
        selectedHeader!.requestNumber,
        userId,
        responsibilityId
      );

      setSaveSuccessMessage("Submitted Successfully");
      setTimeout(() => setSaveSuccessMessage(null), 3000);
      setSelectedHeader(prev => prev ? { ...prev, toSite: headerResult.updatedToSite } : prev);
      setOriginalToSite(headerResult.updatedToSite);
      setScannedLineIds(new Set());
    } catch (error: any) {
      alert(`Submit failed: ${error.message}`);
      setHasSubmitted(false); // Re-enable submit button on error
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    setScanFields({ scanSerialNumber: "", toSubinventory: "", pallet: "", cage: "", tote: "", toLocator: "", trackingNumber: "", toRad: "" });
    setScannedLineIds(new Set());
    setUpdatedHeaders([]); // Clear header changes to revert To Site values
    if (clearLinesErrorsRef.current) {
      clearLinesErrorsRef.current();
    }
  };

  const { fetchValidatedSnItem } = useValidatedSnItem();
  const { validateSerial } = useValidateSerialNumber();

  // Handle edit line from LinesTable
  const handleEditLine = async (line: Line) => {
    // Call getValidatedSnItem API with the serial number from the line
    if (line.serialNumber) {
      try {
        const validatedSnItemResult = await fetchValidatedSnItem(line.serialNumber);
        
        // Check if API response returns any data
        const hasValidatedData = !!(validatedSnItemResult && validatedSnItemResult.itemNumber && validatedSnItemResult.itemNumber.trim());
        
        if (hasValidatedData) {
          // Call validateserialnumber API with fromSite and serial number
          try {
            const validateResult = await validateSerial(
              selectedHeader?.fromSite || '',
              line.serialNumber
            );
            
            // Check if snRepairEligible is "ARMA Eligible"
            if (validateResult && validateResult.length > 0 && validateResult[0].snRepairEligible === 'ARMA Eligible') {
              setEditLineData({ ...line, isSearchReadOnly: true });
            } else {
              // For any other response, keep search editable
              setEditLineData({ ...line, isSearchReadOnly: false });
            }
          } catch (validateError) {
            console.error('Error calling validateserialnumber:', validateError);
            // If validation check fails, allow search (not read-only)
            setEditLineData({ ...line, isSearchReadOnly: false });
          }
        } else {
          // If no validated data, allow search (not read-only)
          setEditLineData({ ...line, isSearchReadOnly: false });
        }
      } catch (error) {
        console.error('Error calling getValidatedSnItem:', error);
        // If API call fails, allow search (not read-only)
        setEditLineData({ ...line, isSearchReadOnly: false });
      }
    } else {
      // If no serial number, allow search (not read-only)
      setEditLineData({ ...line, isSearchReadOnly: false });
    }
    
    setSerialNumberForForm(line.serialNumber);
    setShowNewLineForm(true);
  };

  const handleStartTour = () => {
    const steps = createRadReceivingTourSteps(searchTerm, selectedHeader, currentLines, scanFields, setCurrentStep, isDarkTheme);
    if (setSteps) setSteps(steps);
    if (setCurrentStep) setCurrentStep(savedTourStep);
    if (setIsOpen) setIsOpen(true);
  };

  useEffect(() => {
    if (isOpen && setSteps) {
      const steps = createRadReceivingTourSteps(searchTerm, selectedHeader, currentLines, scanFields, setCurrentStep, isDarkTheme);
      const totalSteps = steps.length;
      if (currentStep === totalSteps - 1) {
        setSavedTourStep(0);
      } else {
        setSavedTourStep(currentStep);
      }
    }
    if (!isOpen && savedTourStep === 0) {
      if (setCurrentStep) setCurrentStep(0);
    }
  }, [currentStep, isOpen, searchTerm, selectedHeader, currentLines, scanFields, setSteps, setCurrentStep, savedTourStep, isDarkTheme]);

  if (!isLoggedIn) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid #f3f3f3",
            borderTop: "4px solid #007bff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite"
          }}
        />
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  const themeStyles = {
    backgroundColor: isDarkTheme ? "#1a1a1a" : "#ffffff",
    color: isDarkTheme ? "#ffffff" : "#000000",
  };

  return (
    <div
      style={{
        ...themeStyles,
        padding: "1px 20px 20px 20px",
        fontFamily: "Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          backgroundColor: isDarkTheme ? "#2c3e50" : "#3498db",
          color: "white",
          padding: "15px 20px",
          margin: "-1px -20px 20px -20px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "bold" }}>RMA Receive & Equipment Return Dashboard</h2>
        <div
          onClick={() => setIsDarkTheme(!isDarkTheme)}
          style={{
            width: "40px",
            height: "40px",
            backgroundColor: isDarkTheme ? "#555" : "#333",
            borderRadius: "50%",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
            transition: "background-color 0.3s",
          }}
        >
          {isDarkTheme ? "☽" : "☀"}
        </div>
      </div>

      <SearchField
        ref={searchFieldRef}
        value={searchTerm}
        onChange={handleSearchTermChange}
        onSearch={handleSearch}
        isDarkTheme={isDarkTheme}
        loading={loading}
        includeClosedOrders={includeClosedOrders}
        onIncludeClosedOrdersChange={setIncludeClosedOrders}
        warningMessage={headerSelectionWarning}
        onWarningClose={() => setHeaderSelectionWarning(null)}
        fromSiteWhid={fromSiteWhid}
        onFromSiteWhidChange={(whid, organizationId) => {
          setFromSiteWhid(whid);
          setFromSiteWhidOrgId(organizationId);
          if (!whid) setSearchTerm('');
        }}
        fromSiteWhidOptions={fromSiteWhidOptions}
      />

      {error && (
        <div style={{ padding: "20px", color: "red" }}>Error: {error}</div>
      )}
      {saveError && (
        <div style={{ padding: "20px", color: "red" }}>Save Error: {saveError}</div>
      )}
      {upsertError && (
        <div style={{ padding: "20px", color: "red" }}>Upsert Error: {upsertError}</div>
      )}
      {submitRequestError && (
        <div style={{ padding: "20px", color: "red" }}>Submit Request Error: {submitRequestError}</div>
      )}
      <HeaderTable
        headers={updatedHeaders.length > 0 ? updatedHeaders : filteredHeaders}
        onHeaderClick={handleHeaderClick}
        selectedHeader={selectedHeader}
        isDarkTheme={isDarkTheme}
        linesLoading={linesLoading}
        onHeaderUpdate={(headerId, field, value, organizationId) => {
          const headersToUpdate = updatedHeaders.length > 0 ? updatedHeaders : filteredHeaders;
          const updatedHeadersList = headersToUpdate.map(h => 
            h.requestNumber === headerId ? { ...h, toSite: value } : h
          );
          setUpdatedHeaders(updatedHeadersList);
          
          // If toSite changed and we have a selected header, refresh pallet LOV
          if (field === 'To Site' && selectedHeader?.requestNumber === headerId && organizationId) {
            console.log('ToSite changed, refreshing pallet LOV for organizationId:', organizationId);
            console.log('Dashboard - Before update - selectedHeaderOrgId:', selectedHeaderOrgId);
            // Clear existing pallet data and refresh
            setScanFields(prev => ({ ...prev, pallet: '', cage: '', tote: '', toSubinventory: '', toLocator: '', toRad: '' }));
            // Update the selected header's toOrg to the new organizationId for ScanFields
            setSelectedHeader(prev => prev ? { ...prev, toOrg: organizationId, toSite: value } : prev);
            // Track the organizationId for locator API calls
            setSelectedHeaderOrgId(organizationId);
            console.log('Dashboard - After update - selectedHeaderOrgId will be:', organizationId);
          }
        }}
        radSites={radSites}
        radSitesLoading={radSitesLoading}
        onLoadRadSites={fetchRadSites}
        onLoadMore={() => loadMoreHeaders(searchTerm, includeClosedOrders, fromSiteWhidOrgId)}
        hasMore={hasMore}
        loading={loading}
        userId={userId}
        onReopenSuccess={(requestNumber) => {
          // Refresh the specific header record after successful reopen
          refreshSingleHeader(requestNumber);
        }}
      />

        <div style={{ marginTop: "10px", marginBottom: "10px" }}>
        <ScanFields
          scanFields={scanFields}
          onScanFieldsChange={setScanFields}
          onSerialNumberScan={serialScanner.scanSerialNumber}
          isDarkTheme={isDarkTheme}
          selectedHeaderToOrg={selectedHeader?.toOrg}
          lines={currentLines}
          clearSerialNotFoundRef={clearSerialNotFoundRef}
          serialScanLoading={serialScanLoading}
          scanSerialError={scanSerialError}
          onFieldClickWithoutHeader={() => {
            setHeaderSelectionWarning("No Repair Order is selected. Please select a Repair Order header record to receive.");
            setTimeout(() => setHeaderSelectionWarning(null), 5000);
            // Focus search input
            setTimeout(() => {
              if (searchFieldRef.current) {
                searchFieldRef.current.focusInput();
              }
            }, 100);
          }}
          radSites={radSites}
          selectedHeader={selectedHeader}
        />
      </div>

      {warningMessage && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: isDarkTheme ? '#8b4513' : '#fff3cd',
          color: '#856404',
          padding: '20px 30px',
          border: '2px solid #ffeaa7',
          borderRadius: '8px',
          fontSize: '18px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '15px',
          minWidth: '300px'
        }}>
          {warningMessage}
          <span
            onClick={() => setWarningMessage(null)}
            style={{
              cursor: 'pointer',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#856404'
            }}
          >
            ×
          </span>
        </div>
      )}

      {linesError && (
        <div style={{ padding: "20px", color: "red" }}>
          Lines Error: {linesError}
        </div>
      )}

      <div style={{ marginBottom: "5px", position: "relative", overflow: "visible" }}>
        {linesLoading ? (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxHeight: "375px",
            overflowY: "auto",
            border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff'
          }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #007bff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite"
              }}
            />
          </div>
        ) : (
          <LinesTable
            lines={currentLines}
            isDarkTheme={isDarkTheme}
            onLineUpdate={lineManager.updateLine}
            selectedHeaderToOrg={selectedHeaderOrgId || selectedHeader?.toOrg}
            newRowIds={lineManager.newRowIds}
            scannedLineIds={scannedLineIds}
            clearErrorsRef={clearLinesErrorsRef}
            toRadFromScanFields={scanFields.toRad}
            radSites={radSites}
            onEditLine={handleEditLine}
            orderType={selectedHeader?.type?.toUpperCase()}
          />
        )}
      </div>

      <ActionButtons
        isDarkTheme={isDarkTheme}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        submitLoading={submitLoading}
        submitDisabled={isSubmitDisabled}
      />

      {saveSuccessMessage && (
        <div style={{
          textAlign: 'center',
          backgroundColor: isDarkTheme ? '#2d5a2d' : '#d4edda',
          color: 'green',
          padding: '8px 16px',
          border: '1px solid green',
          borderRadius: '4px',
          fontSize: '14px',
          margin: '10px auto',
          width: 'fit-content',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {saveSuccessMessage}
        </div>
      )}

      <SerialSelectionPopup
        isOpen={showSerialSelectionPopup}
        onClose={() => {
          setShowSerialSelectionPopup(false);
          setSerialValidationResults([]);
        }}
        onSelect={async (selectedResult) => {
          // Stage remaining items first
          await stageRemainingItems(
            selectedResult, 
            serialValidationResults,
            selectedHeader?.requestNumber || '',
            userId
          );
          
          // Use SerialNumberScanner's processPopupSelection to handle line update/creation
          await serialScanner.processPopupSelection(selectedResult);
          
          setShowSerialSelectionPopup(false);
          setSerialValidationResults([]);
        }}
        results={serialValidationResults}
        isDarkTheme={isDarkTheme}
      />

      <TourWidget onStartTour={handleStartTour} />

      <NewLineForm
        key={showNewLineForm ? `form-${Date.now()}` : 'closed'}
        isOpen={showNewLineForm}
        onClose={() => {
          setShowNewLineForm(false);
          setEditLineData(undefined);
          setIsFromPopupSelection(false);
        }}
        serialNumberFromLines={serialNumberForForm}
        editLineData={editLineData}
        onLineUpdate={lineManager.updateLine}
        onSave={async (newLine: Partial<Line>) => {
          // Helper function to match serial numbers with/without leading 'S'
          const matchesSerial = (scannedSerial: string, lineSerial: string): boolean => {
            const normalizeSerial = (serial: string) => serial.toLowerCase().replace(/^s/, '');
            return normalizeSerial(scannedSerial) === normalizeSerial(lineSerial);
          };
          
          // Create locator if all required fields are present and not already created in NewLineForm
          let locator = newLine.toLocator || '';
          if (!locator && newLine.toPallet && newLine.toCage && newLine.toTote && newLine.toSubinventory && selectedHeader?.toOrg && selectedHeader?.toSite && userId) {
            try {
              const result = await createLocator({
                p_user_id: userId,
                p_organization_name: selectedHeader.toSite,
                p_subinventory: newLine.toSubinventory,
                p_pallet: newLine.toPallet,
                p_cage: newLine.toCage,
                p_tote: newLine.toTote
              });
              if (result.locator) {
                locator = result.locator;
                console.log('Created locator for new line form:', result.locator);
              }
            } catch (error) {
              console.error('Failed to create locator for new line form:', error);
            }
          }
          
          const newLineWithLocator = { ...newLine, toLocator: locator };
          
          // Check if serial number already exists in lines table
          const existingLineIndex = currentLines.findIndex(line => 
            newLineWithLocator.serialNumber && line.serialNumber && matchesSerial(newLineWithLocator.serialNumber, line.serialNumber)
          );
          
          if (existingLineIndex !== -1) {
            // Update existing line
            setUpdatedLines(prev => {
              const baseLines = prev.length > 0 ? prev : headerLines;
              const updatedLines = [...baseLines];
              updatedLines[existingLineIndex] = {
                ...updatedLines[existingLineIndex],
                ...newLineWithLocator
              };
              return updatedLines;
            });
            // Call upsertLines API after line update (moved outside setUpdatedLines)
            callUpsertLinesAPI({
              ...currentLines[existingLineIndex],
              ...newLineWithLocator
            });
            setScannedLineIds(prev => new Set(prev).add(currentLines[existingLineIndex].id));
          } else if (editLineData || isFromPopupSelection) {
            // Don't create new line if editing existing line OR if from popup selection
            // For popup selection, SerialNumberScanner will handle the line creation
            console.log('Editing existing line or from popup selection, not creating new one');
          } else {
            // Create new line only when not editing
            const newLineWithId = {
              ...newLineWithLocator,
              id: `new-${Date.now()}`,
              validatedSerialItem: '',
              returnHeaderId: selectedHeader?.requestNumber || ''
            } as Line;
            setUpdatedLines(prev => {
              const baseLines = prev.length > 0 ? prev : headerLines;
              const updatedLinesList = [newLineWithId, ...baseLines];
              return updatedLinesList;
            });
            // Call upsertLines API after new line creation (moved outside setUpdatedLines)
            callUpsertLinesAPI(newLineWithId);
            setScannedLineIds(prev => new Set(prev).add(newLineWithId.id));
          }
          // Clear serial not found message
          if (clearSerialNotFoundRef.current) {
            clearSerialNotFoundRef.current();
          }
        }}
        scanFields={scanFields}
        isDarkTheme={isDarkTheme}
        selectedHeaderToOrg={selectedHeaderOrgId || selectedHeader?.toOrg}
        selectedHeaderFromSite={selectedHeader?.fromSite}
        selectedHeaderToSite={selectedHeader?.toSite}
        selectedHeaderFromOrg={selectedHeader?.fromOrg}
        toRadFromScanFields={scanFields.toRad}
        userId={userId}
        orderType={selectedHeader?.type?.toUpperCase()}
      />

      {validationError && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#fff',
            color: isDarkTheme ? '#fff' : '#000',
            padding: '30px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '20px', fontSize: '16px' }}>
              {validationError}
            </div>
            <button
              onClick={() => setValidationError(null)}
              style={{
                padding: '8px 20px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  return (
    <ThemeContext.Provider value={{ isDarkTheme }}>
      <style>{`
        [data-tour-elem="controls"] button {
          background-color: transparent !important;
          color: #1976d2 !important;
          font-size: 28px !important;
          font-weight: bold !important;
          padding: 4px 8px !important;
          borderRadius: 4px !important;
          cursor: pointer !important;
          line-height: 1 !important;
        }
        [data-tour-elem="controls"] button:hover {
          background-color: rgba(25, 118, 210, 0.1) !important;
        }
        [data-tour-elem="badge"] {
          background-color: #1976d2 !important;
        }
        [data-tour-elem="dot"] {
          background-color: #1976d2 !important;
        }
      `}</style>
      <TourProvider
        steps={[]}
        styles={{
          popover: (base) => ({
            ...base,
            '--reactour-accent': '#1976d2',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            padding: '25px',
            maxWidth: '400px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#ffffff' : '#000000',
          }),
          maskArea: (base) => ({ ...base, rx: 8 }),
          badge: (base) => ({ ...base, backgroundColor: '#1976d2' }),
          dot: (base) => ({ ...base, backgroundColor: '#1976d2' }),
        }}
        padding={{
          mask: 10,
          popover: [10, 10],
        }}
        scrollSmooth
      >
        <DashboardContent isDarkTheme={isDarkTheme} setIsDarkTheme={setIsDarkTheme} />
      </TourProvider>
    </ThemeContext.Provider>
  );
};

export default Dashboard;
